import React, { useCallback } from 'react'
import { SidebarMenu } from '../../../ducks/app';
import { useNavigation } from '../../../hooks/useNavigation';
import { LocalStorageManager } from '../../../core/storage/LocalStorageManager';
import { AppConfig } from '../../../AppConfig';
import styled from 'styled-components';
import { Logo } from '../../atoms/Logo';
import { SidebarMenus } from '../../molecules/sidebar/SidebarMenus';
import { SidebarFooter } from '../../molecules/sidebar/SidebarFooter';
import { COLOR_PRIMARY, COLOR_WHITE } from '../../../constants/color';

type Props = {
  username: string;
  sidebarMenuToShow: SidebarMenu[];
  selectedMenuPath: string;
  onSidebarMenuClick: (path: string) => void;
  handleLogout: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
};

export const Sidebar: React.FC<Props> = ({
  username,
  sidebarMenuToShow,
  selectedMenuPath,
  onSidebarMenuClick,
  handleLogout,
  isMobileOpen = false,
  onMobileClose
}) => {

  const { navigateToPage } = useNavigation();
  const accessToken = LocalStorageManager.Instance.getObject(
    AppConfig.Instance.LocalStorageKey["accessToken"],
  )

  const onClickLogout = useCallback(() => {
    handleLogout();
  }, [handleLogout])

  const handleMenuClick = useCallback((path: string) => {
    onSidebarMenuClick(path);
    // Close mobile sidebar when menu item is clicked
    if (onMobileClose) {
      onMobileClose();
    }
  }, [onSidebarMenuClick, onMobileClose]);

  return (
    <StyledSideBarContainer $isMobileOpen={isMobileOpen} data-sidebar-content>
      <StyledLogoContainer>
        <Logo width={200} height={70} />
      </StyledLogoContainer>

      <StyledMenusContainer>
        <SidebarMenus
          sidebarMenusToShow={sidebarMenuToShow}
          selectedMenuPath={selectedMenuPath}
          onSidebarMenuClick={handleMenuClick}
        />
      </StyledMenusContainer>

      <StyledFooterContainer>
        <SidebarFooter
          username={username}
          handleLogout={onClickLogout}
          selectedMenuPath={selectedMenuPath}
          onMobileClose={onMobileClose}
        />
      </StyledFooterContainer>
    </StyledSideBarContainer>
  )
}

const StyledSideBarContainer = styled.div<{ $isMobileOpen: boolean }>`
  min-width: 288px;
  /* Use dynamic viewport unit to avoid iOS Safari toolbar issues */
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background-color: ${COLOR_PRIMARY};
  border-right: 1px solid #cccccc;
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: ${({ $isMobileOpen }) => ($isMobileOpen ? '0' : '-100%')};
    width: 280px;
    min-width: 280px;
    z-index: 999;
    transition: left 0.3s ease-in-out;
    box-shadow: ${({ $isMobileOpen }) => ($isMobileOpen ? '2px 0 10px rgba(0, 0, 0, 0.1)' : 'none')};
  }
`;

const StyledLogoContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0 25px 0;
`;

const StyledMenusContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0 20px;
  gap: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`;

const StyledFooterContainer = styled.div`
  padding: 0 20px 25px 20px;
  position: relative;
`;
